import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Paper, Switch, Typography } from '@mui/material';
import { getAuthSettings, updateAuthSettings } from '../../Api/Platform';

const EMPTY = {
  company_sso: { sign_in: false, sign_up: false },
  oauth: { sign_in: false, sign_up: false },
  google: { sign_in: false, sign_up: false },
  microsoft: { sign_in: false, sign_up: false },
  apple: { sign_in: false, sign_up: false },
};

const ROWS = [
  {
    key: 'company_sso',
    title: 'Company SSO',
    detail: 'Entra for a configured email domain. Sign-up is stored and not routed yet.',
  },
  {
    key: 'oauth',
    title: 'OAuth',
    detail: 'Master for Google, Microsoft, and Apple. Off on a side holds those three flags.',
  },
  {
    key: 'google',
    title: 'Google',
    detail: 'Turn sign-in off when Google is broken. Takes effect while OAuth sign-in is on.',
  },
  {
    key: 'microsoft',
    title: 'Microsoft',
    detail: 'Continue with Microsoft. This does not turn off Company SSO.',
  },
  {
    key: 'apple',
    title: 'Apple',
    detail: 'Continue with Apple. Takes effect while OAuth sign-in is on.',
  },
];

const TrinitySystemSettings = () => {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    getAuthSettings()
      .then((res) => {
        if (!cancelled) setOptions({ ...EMPTY, ...(res?.data || {}) });
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || 'Could not load settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (next, label) => {
    const previous = options;
    setSaving(label);
    setError('');
    setNotice('');
    setOptions(next);
    try {
      const res = await updateAuthSettings(next);
      setOptions({ ...EMPTY, ...(res?.data || next) });
      setNotice('Saved for this environment.');
    } catch (err) {
      setOptions(previous);
      setError(err?.response?.data?.error || 'Could not save settings');
    } finally {
      setSaving('');
    }
  };

  const toggle = (key, side) => {
    const pair = options?.[key] || { sign_in: false, sign_up: false };
    save(
      {
        ...options,
        [key]: { ...pair, [side]: !pair[side] },
      },
      `${key}-${side}`
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
        <CircularProgress size={22} />
        <Typography variant="body2" color="text.secondary">
          Loading system settings…
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        System Wide Settings
      </Typography>
      <Typography variant="body2" sx={{ color: '#6B7280', mb: 2, maxWidth: 820 }}>
        Each block on this page is one section. Sign-in and sign-up is the first. Later settings
        get their own section under it. This portal writes the database for this environment only.
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 880 }}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity="success" sx={{ mb: 2, maxWidth: 880 }}>
          {notice}
        </Alert>
      ) : null}
      <Paper variant="outlined" sx={{ p: 2.5, maxWidth: 880 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          Sign-in and sign-up
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
          Sign in is live on the login page. Sign up is saved beside it and does nothing until a
          sign-up route exists. Company SSO is separate from Continue with Microsoft.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, pr: 0.5 }}>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ width: 72, fontWeight: 700, color: '#374151' }}>
            Sign in
          </Typography>
          <Typography variant="caption" sx={{ width: 72, fontWeight: 700, color: '#6B7280' }}>
            Sign up
          </Typography>
        </Box>
        {ROWS.map((row) => {
          const pair = options?.[row.key] || { sign_in: false, sign_up: false };
          return (
            <Box
              key={row.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.25,
                borderTop: '1px solid #E5E7EB',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.title}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }}>
                  {row.detail}
                </Typography>
              </Box>
              <Box sx={{ width: 72 }}>
                <Switch
                  checked={!!pair.sign_in}
                  disabled={!!saving}
                  onChange={() => toggle(row.key, 'sign_in')}
                  inputProps={{ 'aria-label': `${row.title} sign in` }}
                />
              </Box>
              <Box sx={{ width: 72 }}>
                <Switch
                  checked={!!pair.sign_up}
                  disabled={!!saving}
                  onChange={() => toggle(row.key, 'sign_up')}
                  inputProps={{ 'aria-label': `${row.title} sign up` }}
                />
              </Box>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default TrinitySystemSettings;
